import React, { MouseEvent, useCallback, useState, type JSX } from 'react';

import { rem, Menu, Portal, MenuList } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { T_MORE } from '../../translations';
import { stopEventPropagation } from '../../utils/dom/event';
import { createDomName } from '../../utils/dom/identifying';
import { IBokehIconButtonProps } from './BokehIconButton';
import { BokehTooltip } from './BokehTooltip';
import { ThreeDotsMenuButton } from './ThreeDotsMenuButton';

export interface IBokehMenuProps extends IBaseDomElementProps {
  isOpen: boolean;
  isDisabled?: boolean;
  onClick: () => void;
  onClose: () => void;
  menuList: JSX.Element;
  placement?: string;
  isInPortal?: boolean;
  variant?: IBokehIconButtonProps['variant'];
}

export function BokehMenu({
  isOpen,
  isDisabled,
  onClick,
  id = '',
  className = '',
  onClose,
  menuList,
  placement = 'right-start',
  isInPortal = true,
  variant,
}: IBokehMenuProps) {
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const onThreeDotsClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      stopEventPropagation(event);
      setTooltipOpen(false);
      onClick();
    },
    [onClick],
  );

  return (
    <Menu isOpen={isOpen} onClose={onClose} placement={placement} gutter={16} closeOnSelect={false}>
      <BokehTooltip isOpen={tooltipOpen} label={T_MORE}>
        <ThreeDotsMenuButton
          id={createDomName(id, 'button')}
          className={className}
          variant={variant}
          isDisabled={isDisabled}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onClick={onThreeDotsClick}
        />
      </BokehTooltip>

      {isInPortal ? (
        <Portal>
          <MenuList id={createDomName(id, 'popover')} overflow={'hidden'} minWidth={rem(142)}>
            {menuList}
          </MenuList>
        </Portal>
      ) : (
        <MenuList id={createDomName(id, 'popover')} overflow={'hidden'} minWidth={rem(142)}>
          {menuList}
        </MenuList>
      )}
    </Menu>
  );
}
